import request from '@/Network/Requests/request'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import Service from '@/Network/Services/Service'
import type {
  AnnouncementListQuery,
  AnnouncementRecipientsPreviewQuery,
  CreateAnnouncementRequest
} from '@/Network/Types/Requests/AnnouncementRequests'
import type {
  AnnouncementDetailResponse,
  AnnouncementListResponse,
  AnnouncementRecipientsPreviewResponse,
  CreateAnnouncementResponse
} from '@/Network/Types/Responses/AnnouncementResponses'

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** Query do preview: `scheduleIds` em CSV (`1,2`) e período só quando informado. */
export const buildRecipientsPreviewParams = (
  query: AnnouncementRecipientsPreviewQuery
): Record<string, string> => {
  const params: Record<string, string> = { scheduleIds: query.scheduleIds.join(',') }
  if (query.from) params.from = query.from
  if (query.to) params.to = query.to
  return params
}

/** Query da lista: só campos definidos sobem. */
export const buildAnnouncementListParams = (
  query: AnnouncementListQuery
): Record<string, string> => {
  const params: Record<string, string> = {}
  if (query.scheduleId !== undefined) params.scheduleId = String(query.scheduleId)
  if (query.page !== undefined) params.page = String(query.page)
  if (query.limit !== undefined) params.limit = String(query.limit)
  return params
}

/**
 * Comunicados com recibo de leitura/ciência (V-b, PoC Novo Hamburgo).
 * Contrato: `briefs/poc-novo-hamburgo/CONTRACT.md`. Com a flag do backend
 * (`ENABLED_ANNOUNCEMENTS`) desligada todos os endpoints respondem 404.
 */
export default class AnnouncementService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/announcements'
  }

  async previewRecipients(
    query: AnnouncementRecipientsPreviewQuery
  ): Promise<AnnouncementRecipientsPreviewResponse> {
    try {
      const { data } = await request.get<AnnouncementRecipientsPreviewResponse>(
        this.getCompletePath('/recipients-preview'),
        buildRecipientsPreviewParams(query)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os destinatários do comunicado')
    }
  }

  async create(payload: CreateAnnouncementRequest): Promise<CreateAnnouncementResponse> {
    try {
      const { data } = await request.post<CreateAnnouncementResponse>(
        this.getCompletePath(),
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível enviar o comunicado')
    }
  }

  async list(query: AnnouncementListQuery): Promise<AnnouncementListResponse> {
    try {
      const { data } = await request.get<AnnouncementListResponse>(
        this.getCompletePath(),
        buildAnnouncementListParams(query)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os comunicados')
    }
  }

  async getById(id: number): Promise<AnnouncementDetailResponse> {
    try {
      const { data } = await request.get<AnnouncementDetailResponse>(this.getCompletePath(`/${id}`))
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o comunicado')
    }
  }

  /**
   * Download do xlsx no padrão do painel (`PriorityPaymentsService.exportXlsx`):
   * `responseType: 'blob'`, nome do `content-disposition`, âncora temporária.
   */
  async exportXlsx(id: number): Promise<void> {
    try {
      const { data, headers } = await request.get<BlobPart>(
        this.getCompletePath(`/${id}/export`),
        undefined,
        undefined,
        'blob'
      )
      const disposition = (headers?.['content-disposition'] as string | undefined) ?? ''
      const filename =
        disposition.match(/filename="?([^"]+)"?/)?.[1] ?? `comunicado-${id}-recibos.xlsx`
      const url = window.URL.createObjectURL(new Blob([data], { type: XLSX_CONTENT_TYPE }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar os recibos do comunicado'
      )
    }
  }
}
